import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { AdsAdvertiserItem } from '@brand24-original/llm-types';
import { AdsFilters, getAdsAdvertisers } from '../../services/ads/AdsService';
import { GET_ADS_ADVERTISERS } from '../../query/ads/keys';

/**
 * Advertisers ranking. Deliberately NOT advertiser-filtered — it feeds the advertiser filter
 * dropdown (needs the full list) and the ranking table (shares stay global); the page filters
 * the displayed rows client-side when one advertiser is selected.
 */
export const useGetAdsAdvertisers = (
  projectId: number | null,
  from: Date | null,
  to: Date | null,
  filters: Omit<AdsFilters, 'advertiser'>,
  enabled = true
) => {
  const queryEnabled = !!projectId && !!from && !!to && enabled;

  const { data, isLoading, isError, refetch } = useQuery<AdsAdvertiserItem[]>({
    queryKey: [GET_ADS_ADVERTISERS, projectId, from, to, filters.models, filters.promptIds],
    queryFn: () => getAdsAdvertisers(projectId!, from!, to!, filters),
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
    enabled: queryEnabled,
    placeholderData: keepPreviousData,
  });

  return {
    advertisers: queryEnabled ? data ?? [] : [],
    isLoading: queryEnabled ? isLoading : false,
    isError,
    refetch,
  };
};
